import { pageTipRouting, tipSuppressedPaths } from '@/config/pageTips';
import { type SiteTip, type SiteTipId, siteTips } from '@/content/tips';

import { matchLongestPathEntry, pathHasPrefix } from './routeMatch';

/**
 * A tip with its id and the route key that found it.
 *
 * `key` is what a dismissal is remembered against, and it is the route rather
 * than the tip or the exact pathname on purpose. The tip id would be the same
 * thing today — the map is one-to-one — but it would quietly go back to
 * silencing several pages at once the moment two of them shared a message
 * again. The full pathname would go the other way and treat every car in the
 * catalogue as its own page, so a visitor browsing ten of them would meet the
 * same card ten times. The route key is the unit a visitor actually
 * experiences as "this page".
 */
export type ResolvedPageTip = SiteTip & { id: SiteTipId; key: string };

/**
 * Which tip belongs on this page, or null for no tip at all.
 *
 * Pure, so the routing can be read and tested without rendering anything;
 * `hooks/useScrollNudge.ts` is the React on top. The twin of
 * `resolveWhatsAppContact`, and deliberately shaped like it.
 *
 * Suppression is checked **first**, which is the one ordering decision in this
 * file and the reason the list in `config/pageTips.ts` can be trusted. A path
 * on it cannot be nudged however the routing map above it is edited later —
 * including by a future catch-all key, and including by a mistake.
 *
 * Null for everything else too, which is the right default: a page earns a tip
 * by being named, so a route added to the site next month is quiet until
 * somebody decides what it should say. That also covers the 404, which has no
 * path to name.
 */
export function resolvePageTip(pathname: string): ResolvedPageTip | null {
  if (pathHasPrefix(pathname, tipSuppressedPaths)) return null;

  const matched = matchLongestPathEntry(pathname, pageTipRouting);
  if (!matched) return null;

  return { id: matched.value, key: matched.key, ...siteTips[matched.value] };
}
